<template>
	<div id="publishers-howto" ref="sectionRef" class="howto">
		<div class="howto__wrapper">
			<div class="howto__container container">
				<h2 class="howto__title heading h2">
					{{ $t('pages.publishers.sections.howto.title') }}
				</h2>
				<div class="howto__content">
					<template v-for="(item, i) in steps" :key="i">
						<div class="howto__card">
							<img
								v-if="$rt(item.img)"
								class="howto__card-img"
								:src="$rt(item.img)"
								alt="howtoImg"
								loading="lazy"
								decoding="async" />
							<div class="howto__card-content">
								<p class="howto__card-title">
									{{ $rt(item.title) }}
								</p>
								<p class="howto__card-text">
									{{ $rt(item.description) }}
								</p>
								<button v-if="item.button" class="howto__card-btn" @click="publisherModal.openModal">
									{{ $rt(item.button) }}
								</button>
							</div>
						</div>
						<div v-if="i !== steps.length - 1" class="howto__arrow">
							<img
								src="/images/pages/publishers/howto/howto-arrow.png"
								alt="howtoArrow"
								loading="lazy"
								decoding="async" />
						</div>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	interface HowToStep {
		img: string
		title: string
		description: string
		button?: string
	}

	const { tm } = useI18n()
	const { $gsap: gsap } = useNuxtApp()

	const publisherModal = usePublisherModal()

	const steps = computed(() => tm('pages.publishers.sections.howto.steps') as HowToStep[])
	const sectionRef = ref<HTMLElement | null>(null)
	let mainTl: gsap.core.Timeline | null = null

	onMounted(() => {
		const sectionEl = sectionRef.value
		if (!sectionEl) return

		const cards = sectionEl.querySelectorAll<HTMLElement>('.howto__card')
		const images = sectionEl.querySelectorAll<HTMLElement>('.howto__card-img')
		const arrows = sectionEl.querySelectorAll<HTMLElement>('.howto__arrow')

		if (!cards.length) return

		gsap.set(cards, { autoAlpha: 0, y: 20 })
		gsap.set(images, { autoAlpha: 0, scale: 0 })
		gsap.set(arrows, { autoAlpha: 0, x: 0 })

		mainTl = gsap.timeline({
			scrollTrigger: {
				trigger: sectionEl,
				start: 'top 80%',
				end: 'bottom bottom',
				scrub: 1,
			},
		})

		const firstCard = cards[0]
		const firstImage = images[0]
		if (!firstCard || !firstImage) return

		// Первая карточка
		mainTl
			.to(firstCard, { autoAlpha: 1, y: 0, duration: 1, ease: 'power2.out' })
			.to(firstImage, { autoAlpha: 1, scale: 1, duration: 1, ease: 'power2.out' }, '<')

		// Стрелка → карточка последовательно
		arrows.forEach((arrow, i) => {
			const nextCard = cards[i + 1]
			const nextImage = images[i + 1]
			if (!nextCard || !nextImage) return

			mainTl!
				.to(arrow, { autoAlpha: 1, x: 0, duration: 0.6, ease: 'power2.out' })
				.to(nextCard, { autoAlpha: 1, y: 0, duration: 1, ease: 'power2.out' })
				.to(nextImage, { autoAlpha: 1, scale: 1, duration: 1, ease: 'power2.out' }, '<')
		})
	})

	onBeforeUnmount(() => {
		mainTl?.scrollTrigger?.kill()
		mainTl?.kill()
		mainTl = null
	})
</script>

<style scoped lang="scss">
	.howto {
		position: relative;
		z-index: 1;
		padding: 0 50px;
		background-color: #f5f5f5;
		border-radius: 90px;

		@media (width <= 1199px) {
			padding: 0 16px;
			border-radius: 20px;
		}
		&__title {
			text-transform: uppercase;
		}
		&__wrapper {
			padding: 64px 0 100px;

			@media (width <= 1199px) {
				padding: 40px 0 60px;
			}
		}
		&__content {
			display: flex;
			gap: 24px;
			align-items: stretch;
			padding-top: 64px;

			@media (width <= 1199px) {
				flex-direction: column;

				// gap: 0;
				gap: 32px;
				align-items: center;
				padding-top: 40px;
			}
		}
	}
	.howto__card {
		display: flex;
		flex: 1 1 0;
		flex-direction: column;
		max-width: 350px;

		@media (width <= 1199px) {
			width: 100%;
			max-width: 100%;
		}
		&-img {
			z-index: 1;
			width: 100%;
			height: 200px;
			margin-bottom: -100px;
			object-fit: contain;

			@media (width <= 1199px) {
				height: 160px;
				margin-bottom: -80px;
			}
		}
		&-content {
			display: flex;
			flex-direction: column;
			height: 100%;
			padding-top: 110px;
			overflow: hidden;
			text-align: center;
			background-color: var(--white);
			border-radius: 20px;

			@media (width <= 1199px) {
				padding-top: 90px;
			}
		}
		&-title {
			padding: 0 16px;
			margin-bottom: 16px;
			font-size: 24px;
			font-weight: bold;

			@media (width <= 1199px) {
				font-size: 20px;
			}
		}
		&-text {
			padding: 0 16px;
			margin-bottom: 32px;
			font-size: 20px;

			@media (width <= 1199px) {
				font-size: 16px;
			}
		}
		&-btn {
			width: 100%;
			padding: 16px;
			margin-top: auto;
			font-weight: bold;
			background-color: var(--primary);
			transition: background-color 0.3s;
			&:hover {
				background-color: var(--primary-hover);
			}
			&:active {
				background-color: var(--primary-active);
			}
		}
	}
	.howto__arrow {
		display: flex;
		flex: 0 0 auto;
		align-items: center;

		@media (width <= 1199px) {
			justify-content: center;
			padding: 8px 0;
		}
		img {
			width: 100px;
			height: auto;
			object-fit: contain;

			@media (width <= 1199px) {
				// width: 40px;
				transform: rotate(90deg);
			}
		}
	}
</style>
